import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { classNames } from "primereact/utils";
import styles from "./DateTimeSelectionStep.module.scss";
import { CalendarView } from "./CalendarView/CalendarView";
import { TimeSlots } from "./TimeSlots/TimeSlots";
import { NoAvailabilityEmptyState } from "./NoAvailabilityEmptyState/NoAvailabilityEmptyState";
import { IService } from "../../models/IService";
import { IEmployeeAvailability } from "../../models";
import {
  useStudioWorkingHours,
  useBulkAppointmentAvailability,
  useDateTimeStepEmptyState,
  IDateAvailability,
} from "../../hooks";
import { useAppContext } from "../../../../../AppContext";
import { callPhoneNumber } from "../../../../../common/infrastructure/routes/routePaths";

interface IDateTimeSelectionStepProps {
  className?: string;
  selectedService?: IService | null;
  selectedEmployee?: IEmployeeAvailability | null;
  selectedDate?: Date | null;
  selectedTime?: string | null;
  selectedEventId?: number | null;
  onDateSelect: (date: Date) => void;
  onTimeSelect: (time: string, eventId?: number) => void;
  onJumpToFirstAvailable: (dateAvailabilityMap?: Map<string, any>) => boolean;
  hasAnyAvailableSlots: (dateAvailabilityMap?: Map<string, any>) => boolean;
  isLoading?: boolean;
  // Optional props for providing pre-fetched availability data (e.g., for group classes)
  dateAvailabilityMap?: Map<string, IDateAvailability>;
  hasAvailableSlots?: (date: Date) => boolean;
  getDateAvailability?: (date: Date) => IDateAvailability | undefined;
  bulkAvailabilityLoading?: boolean;
  bulkAvailabilityHasData?: boolean;
  bulkAvailabilityVisibleDaysCount?: number;
  // Optional custom labels for group class mode
  serviceLabelText?: string;
  employeeLabelText?: string;
}

export const DateTimeSelectionStep = ({
  className,
  selectedService,
  selectedEmployee,
  selectedDate,
  selectedTime,
  selectedEventId,
  onDateSelect,
  onTimeSelect,
  onJumpToFirstAvailable,
  hasAnyAvailableSlots,
  isLoading = false,
  // Optional pre-fetched availability data
  dateAvailabilityMap: providedDateAvailabilityMap,
  hasAvailableSlots: providedHasAvailableSlots,
  getDateAvailability: providedGetDateAvailability,
  bulkAvailabilityLoading: providedBulkLoading,
  bulkAvailabilityHasData: providedBulkHasData,
  bulkAvailabilityVisibleDaysCount: providedVisibleDaysCount,
  // Optional custom labels
  serviceLabelText = "Услуга:",
  employeeLabelText = "Специалист:",
}: IDateTimeSelectionStepProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const { selectedStudio } = useAppContext();
  const timeSlotsSectionRef = useRef<HTMLDivElement>(null);

  // Fetch real studio working hours
  const { studioHours, hasData, isLoading: studioHoursLoading } = useStudioWorkingHours(selectedStudio?.id);

  // Calculate start date for bulk availability (today)
  const today = useMemo(() => {
    const currentDate = new Date();
    // Normalize today's date to avoid timezone issues
    const dateKey = currentDate.toISOString().split("T")[0];
    return new Date(dateKey + "T12:00:00");
  }, []);

  // Fetch individual employee bulk availability (only if not provided via props)
  const shouldFetchAvailability = !providedDateAvailabilityMap;

  const {
    dateAvailabilityMap: fetchedDateAvailabilityMap,
    hasAvailableSlots: fetchedHasAvailableSlots,
    getDateAvailability: fetchedGetDateAvailability,
    isLoading: fetchedAvailabilityLoading,
    hasData: fetchedAvailabilityHasData,
    visibleDaysCount: fetchedVisibleDaysCount,
  } = useBulkAppointmentAvailability(
    shouldFetchAvailability ? selectedEmployee?.id : undefined,
    shouldFetchAvailability ? selectedService?.id : undefined,
    shouldFetchAvailability ? today : undefined,
    shouldFetchAvailability ? selectedStudio?.id : undefined
  );

  // Use provided props or fetched data
  const dateAvailabilityMap = providedDateAvailabilityMap || fetchedDateAvailabilityMap;
  const hasEmployeeAvailableSlots = providedHasAvailableSlots || fetchedHasAvailableSlots;
  const getEmployeeDateAvailability = providedGetDateAvailability || fetchedGetDateAvailability;
  const individualAvailabilityLoading = providedBulkLoading ?? fetchedAvailabilityLoading;
  const individualAvailabilityHasData = providedBulkHasData ?? fetchedAvailabilityHasData;
  const visibleDaysCount = providedVisibleDaysCount ?? fetchedVisibleDaysCount;

  // Helper to check if employee has available slots for a date
  // Use the provided function if available (e.g., for group classes), otherwise use fetched data
  const hasAvailableSlotsForDate = (date: Date): boolean => {
    if (providedHasAvailableSlots) {
      return providedHasAvailableSlots(date);
    }
    if (!selectedEmployee?.id || !hasEmployeeAvailableSlots) {
      return false;
    }
    return hasEmployeeAvailableSlots(date);
  };

  // Get available slots for currently selected date from individual bulk data
  const availableSlots = useMemo(() => {
    if (
      !selectedDate ||
      !selectedEmployee?.id ||
      !getEmployeeDateAvailability
    ) {
      return [];
    }

    const dateAvailability = getEmployeeDateAvailability(selectedDate);
    return dateAvailability?.timeSlots || [];
  }, [selectedDate, selectedEmployee?.id, getEmployeeDateAvailability]);

  // Determine loading state - use individual bulk loading
  const isBulkLoading = individualAvailabilityLoading;

  // Memoized no-arg wrapper around the parent's hasAnyAvailableSlots, bound to
  // the currently visible dateAvailabilityMap. Used by the empty-state hook.
  const hasAnyAvailableSlotsForCurrentMap = useCallback(
    () => hasAnyAvailableSlots(dateAvailabilityMap),
    [hasAnyAvailableSlots, dateAvailabilityMap]
  );

  const handleCallStudio = useCallback(() => {
    callPhoneNumber(selectedStudio?.phoneNumber);
  }, [selectedStudio?.phoneNumber]);

  const { showLoading, showNoStudioHours, showNoAvailability } =
    useDateTimeStepEmptyState({
      isLoading,
      studioHoursLoading,
      isBulkLoading,
      hasStudioHours: hasData && !!studioHours,
      bulkAvailabilityHasData: individualAvailabilityHasData,
      hasAnyAvailableSlots: hasAnyAvailableSlotsForCurrentMap,
    });

  // Track if we've done the initial date selection
  const hasInitiallySelectedRef = useRef(false);
  const hasScrolledRef = useRef(false);

  // Scroll to time slots when a date gets selected (including auto-jump)
  useEffect(() => {
    if (selectedDate && !hasScrolledRef.current) {
      hasScrolledRef.current = true;
      scrollToTimeSlots(300);
    }
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select the first available day once availability data is loaded
  useEffect(() => {
    // Only do this once when data first loads
    // Skip if we already have a valid date and time selection (e.g., navigating back)
    if (
      !hasInitiallySelectedRef.current &&
      individualAvailabilityHasData &&
      dateAvailabilityMap &&
      dateAvailabilityMap.size > 0 &&
      !selectedDate &&
      !selectedTime
    ) {
      hasInitiallySelectedRef.current = true;
      onJumpToFirstAvailable(dateAvailabilityMap);
    }
  }, [individualAvailabilityHasData, dateAvailabilityMap, onJumpToFirstAvailable, selectedDate, selectedTime]);

  const scrollToTimeSlots = (delay = 50) => {
    if (window.innerWidth >= 768 || !timeSlotsSectionRef.current) return;
    setTimeout(() => {
      const el = timeSlotsSectionRef.current;
      if (!el) return;
      const scrollContainer = el.closest<HTMLElement>('[class*="wizardContent"]');
      if (!scrollContainer) return;

      const containerTop = scrollContainer.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      const targetY = scrollContainer.scrollTop + (elTop - containerTop) - 16;
      const startY = scrollContainer.scrollTop;
      const distance = targetY - startY;
      const duration = 900;
      let startTime: number | null = null;

      const easeInOutCubic = (t: number) =>
        t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      const step = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        scrollContainer.scrollTop = startY + distance * easeInOutCubic(progress);
        if (progress < 1) requestAnimationFrame(step);
      };

      requestAnimationFrame(step);
    }, delay);
  };

  const handleDateSelect = (date: Date) => {
    onDateSelect(date);
    scrollToTimeSlots(50);
  };

  const handleTimeSelect = (time: string, eventId?: number) => {
    onTimeSelect(time, eventId);
  };

  const handleJumpToFirstAvailable = () => {
    onJumpToFirstAvailable(dateAvailabilityMap);
    // If no slot was found, the parent component will handle showing the appropriate message
  };

  if (showLoading) {
    return (
      <div className={classNames(styles.container, className)}>
        <div className={styles.header}>
          <h2 className={styles.title}>Изберете дата и час</h2>
          <p className={styles.subtitle}>
            Изберете подходящи дата и час за вашата резервация
          </p>
        </div>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p className={styles.loadingText}>Зареждане на свободни часове...</p>
        </div>
      </div>
    );
  }

  // Show message if no studio working hours are available
  if (showNoStudioHours) {
    return (
      <div className={classNames(styles.container, className)}>
        <div className={styles.header}>
          <h2 className={styles.title}>Изберете дата и час</h2>
          <p className={styles.subtitle}>
            Изберете подходящи дата и час за вашата резервация
          </p>
        </div>
        <div className={styles.noSlotsContainer}>
          <div className={styles.noSlotsIcon}>⏰</div>
          <p className={styles.noSlotsText}>
            Работните часове на обекта не са налични в момента. Моля опитайте
            отново по-късно.
          </p>
        </div>
      </div>
    );
  }

  if (showNoAvailability) {
    return (
      <div className={classNames(styles.container, className)}>
        <div className={styles.header}>
          <h2 className={styles.title}>Изберете дата и час</h2>
          <p className={styles.subtitle}>
            Изберете подходящи дата и час за вашата резервация
          </p>
        </div>
        <NoAvailabilityEmptyState
          phoneNumber={selectedStudio?.phoneNumber}
          onCallStudio={handleCallStudio}
        />
      </div>
    );
  }

  return (
    <div className={classNames(styles.container, className)}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Изберете дата и час</h2>
        <p className={styles.subtitle}>
          Изберете подходящи дата и час за вашата резервация
        </p>
      </div>

      <div className={styles.content_wrapper}>
        {/* Selected Service & Employee Info */}
        {(selectedService || selectedEmployee) && (
          <div className={styles.selectionSummary}>
            {selectedService && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>{serviceLabelText}</span>
                <span className={styles.summaryValue}>
                  {selectedService.name}
                </span>
              </div>
            )}
            {selectedEmployee && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>{employeeLabelText}</span>
                <span className={styles.summaryValue}>
                  {selectedEmployee.name}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Calendar and Time Slots Layout */}
        <div className={styles.contentLayout}>
          {/* Calendar */}
          <div className={styles.calendarSection}>
            <CalendarView
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
              className={styles.calendar}
              studioHours={studioHours}
              hasRealData={hasData}
              hasAvailableSlots={hasAvailableSlotsForDate}
              numberOfDays={visibleDaysCount || 14}
            />
          </div>

          {/* Time Slots */}
          <div className={styles.timeSlotsSection} ref={timeSlotsSectionRef}>
            {selectedDate && availableSlots.length > 0 ? (
              <TimeSlots
                availableSlots={availableSlots}
                selectedTime={selectedTime}
                selectedEventId={selectedEventId}
                onTimeSelect={handleTimeSelect}
                selectedDate={selectedDate}
                className={styles.timeSlots}
              />
            ) : selectedDate && availableSlots.length === 0 ? (
              <div className={styles.noSlotsContainer}>
                <div className={styles.noSlotsIcon}>📅</div>
                <p className={styles.noSlotsText}>
                  Няма свободни часове за{" "}
                  {selectedDate.toLocaleDateString("bg-BG")}
                </p>
                {hasAnyAvailableSlots(dateAvailabilityMap) ? (
                  <button
                    className={styles.jumpButton}
                    onClick={handleJumpToFirstAvailable}
                    type="button"
                  >
                    Отиди до първия свободен ден
                  </button>
                ) : (
                  <p className={styles.noSlotsText}>
                    За момента няма свободни часове, моля свържете се с обекта
                  </p>
                )}
              </div>
            ) : (
              <div className={styles.selectDatePrompt}>
                <p className={styles.promptText}>
                  Изберете дата, за да видите свободните часове
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
